import { useState } from 'react';
import {
  Stack, Button, Chip, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, Typography, Autocomplete,
} from '@mui/material';
import { Add, SwapHoriz } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { FileUploadField } from '../../components/FileUploadField';
import { assetManagementApi, assetTransfersApi, type AmAssetTransfer, type TransferToType, type TransferAction } from '../../api/assetManagement.api';
import { amStationsApi } from '../../api/amStations.api';
import { warehousesApi } from '../../api/inventory.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const human = (s?: string | null) => (s ? s.replace(/_/g, ' ') : '—');
const statusColor: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  pending: 'warning', approved: 'success', rejected: 'error', cancelled: 'default',
};
const TO_TYPES: { value: TransferToType; label: string }[] = [
  { value: 'site', label: 'Site' }, { value: 'warehouse', label: 'Warehouse' },
  { value: 'employee', label: 'Employee' }, { value: 'vendor', label: 'Vendor' }, { value: 'technician', label: 'Technician' },
];

export function AssetTransfersPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const params = { page, limit, status: status || undefined };
  const { data, isLoading } = useQuery({ queryKey: ['am-transfers', params], queryFn: () => assetTransfersApi.list(params) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['am-transfers'] });
  const canManage = hasPermission('am_asset.transfer');

  const decideMut = useMutation({
    mutationFn: (v: { id: number; decision: TransferAction }) => assetTransfersApi.decide(v.id, v.decision),
    onSuccess: () => { setToast('Transfer updated'); refresh(); qc.invalidateQueries({ queryKey: ['am-assets'] }); },
    onError: (e) => setToast(extractApiError(e)),
  });

  const columns: Column<AmAssetTransfer>[] = [
    { key: 'transferCode', label: 'Code', width: 130 },
    { key: 'assetName', label: 'Asset', render: (r) => <span>{r.assetName ?? `#${r.assetId}`}<br /><Typography variant="caption" color="text.secondary">{r.assetCode}</Typography></span> },
    { key: 'route', label: 'From → To', render: (r) => <span>{r.fromLocation ?? human(r.fromType)} <SwapHoriz fontSize="inherit" sx={{ verticalAlign: 'middle' }} /> <strong>{r.toName}</strong> <Typography variant="caption" color="text.secondary">({human(r.toType)})</Typography></span> },
    { key: 'transferDate', label: 'Date', render: (r) => (r.transferDate ? new Date(r.transferDate).toLocaleDateString('en-IN') : '—') },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" color={statusColor[r.status]} label={human(r.status)} /> },
    {
      key: 'actions', label: '', width: 220, render: (r) => (r.status === 'pending' && canManage ? (
        <Stack direction="row" spacing={0.5}>
          <Button size="small" color="success" onClick={() => decideMut.mutate({ id: r.id, decision: 'approve' })}>Approve</Button>
          <Button size="small" color="error" onClick={() => decideMut.mutate({ id: r.id, decision: 'reject' })}>Reject</Button>
          <Button size="small" onClick={() => decideMut.mutate({ id: r.id, decision: 'cancel' })}>Cancel</Button>
        </Stack>
      ) : (r.decisionRemarks ? <Typography variant="caption" color="text.secondary">{r.decisionRemarks}</Typography> : null)),
    },
  ];

  return (
    <>
      <PageHeader title="Asset Transfers" subtitle="Transfer assets between sites, warehouses & custodians — with approval."
        action={canManage ? <Button startIcon={<Add />} variant="contained" onClick={() => setOpen(true)}>New Transfer</Button> : undefined} />
      <Stack direction="row" spacing={1.5} mb={2}>
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 180 }}>
          <MenuItem value="">All</MenuItem>
          {['pending', 'approved', 'rejected', 'cancelled'].map((s) => <MenuItem key={s} value={s}>{human(s)}</MenuItem>)}
        </TextField>
      </Stack>
      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id} />

      {open && <TransferRequestDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); setToast('Transfer requested'); refresh(); }} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

/** Shared transfer-request dialog. Pass a fixed assetId (from the asset detail) or let the user pick one. */
export function TransferRequestDialog({ assetId, onClose, onDone }: { assetId?: number; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ assetId: assetId ?? ('' as number | ''), toType: 'site' as TransferToType, toRefId: '' as number | '', toName: '', transferDate: '', reason: '', documentUrl: '' });
  const [error, setError] = useState<string | null>(null);

  const assets = useQuery({ queryKey: ['am-assets', 'transfer-pick'], enabled: !assetId, queryFn: () => assetManagementApi.list({ limit: 500 }) });
  const stations = useQuery({ queryKey: ['am-stations', 'options'], queryFn: () => amStationsApi.list({ limit: 500, sortBy: 'stationName', sortDir: 'asc' }) });
  const warehouses = useQuery({ queryKey: ['warehouses-lookup'], queryFn: () => warehousesApi.list({ limit: 500 }) });
  const isCustodian = form.toType === 'employee' || form.toType === 'vendor' || form.toType === 'technician';

  const mut = useMutation({
    mutationFn: () => assetTransfersApi.request({
      assetId: Number(form.assetId), toType: form.toType,
      toRefId: !isCustodian && form.toRefId ? Number(form.toRefId) : (isCustodian && form.toRefId ? Number(form.toRefId) : undefined),
      toName: isCustodian ? form.toName : undefined,
      transferDate: form.transferDate || undefined, reason: form.reason || undefined, documentUrl: form.documentUrl || undefined,
    }),
    onSuccess: onDone, onError: (e) => setError(extractApiError(e)),
  });

  const canSubmit = Boolean(form.assetId) && (isCustodian ? form.toName.trim().length > 0 : Boolean(form.toRefId)) && !mut.isPending;

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Request Asset Transfer</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          {!assetId && (
            <Autocomplete
              size="small" options={(assets.data?.data ?? []).filter((a) => a.status !== 'retired')}
              getOptionLabel={(a) => `${a.name} (${a.assetCode})`}
              value={(assets.data?.data ?? []).find((a) => a.id === form.assetId) ?? null}
              onChange={(_, v) => setForm({ ...form, assetId: v ? v.id : '' })}
              renderInput={(p) => <TextField {...p} label="Asset *" />}
            />
          )}
          <TextField select label="Transfer to *" value={form.toType} onChange={(e) => setForm({ ...form, toType: e.target.value as TransferToType, toRefId: '', toName: '' })} fullWidth>
            {TO_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          {form.toType === 'site' && (
            <TextField select label="Destination Site *" value={form.toRefId} onChange={(e) => setForm({ ...form, toRefId: e.target.value ? Number(e.target.value) : '' })} fullWidth>
              {(stations.data?.data ?? []).map((s) => <MenuItem key={s.id} value={s.id}>{s.stationName}{s.city ? ` — ${s.city}` : ''}</MenuItem>)}
            </TextField>
          )}
          {form.toType === 'warehouse' && (
            <TextField select label="Destination Warehouse *" value={form.toRefId} onChange={(e) => setForm({ ...form, toRefId: e.target.value ? Number(e.target.value) : '' })} fullWidth>
              {(warehouses.data?.data ?? []).map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
            </TextField>
          )}
          {isCustodian && (
            <Stack direction="row" spacing={2}>
              <TextField label="Custodian Name *" value={form.toName} onChange={(e) => setForm({ ...form, toName: e.target.value })} fullWidth />
              <TextField label="Custodian ID" type="number" value={form.toRefId} onChange={(e) => setForm({ ...form, toRefId: e.target.value ? Number(e.target.value) : '' })} sx={{ width: 130 }} helperText="optional" />
            </Stack>
          )}
          <Stack direction="row" spacing={2}>
            <TextField label="Transfer Date" type="date" InputLabelProps={{ shrink: true }} value={form.transferDate} onChange={(e) => setForm({ ...form, transferDate: e.target.value })} fullWidth />
            <TextField label="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} fullWidth />
          </Stack>
          <FileUploadField label="Transfer document (optional)" accept=".pdf,image/*" value={form.documentUrl} onChange={(url) => setForm({ ...form, documentUrl: url ?? '' })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => mut.mutate()}>Request Transfer</Button>
      </DialogActions>
    </Dialog>
  );
}
